<script setup lang="ts">
/**
 * Skeleton para cards de Missions - imita o layout real do MissionDisponiveis
 * Uso: <SmarticoApostatudoSkeletonMissionsSkeleton :count="6" />
 */

interface Props {
  /** Quantidade de items skeleton */
  count?: number;
}

withDefaults(defineProps<Props>(), {
  count: 6,
});
</script>

<template>
  <div class="missions-skeleton">
    <div
      v-for="n in count"
      :key="`skeleton-${n}`"
      class="skeleton-card-wrapper"
    >
      <div class="skeleton-card">
        <!-- Header: Imagem + Info (igual ao missionBlock) -->
        <div class="skeleton-mission-block">
          <div class="skeleton-image" />
          <div class="skeleton-info">
            <div class="skeleton-name" />
            <div class="skeleton-name-line2" />
            <div class="skeleton-description" />
            <div class="skeleton-description-line2" />
          </div>
        </div>

        <!-- Recompensa (rewardSection) -->
        <div class="skeleton-reward" />

        <!-- Botões (buttonsSection) -->
        <div class="skeleton-buttons">
          <div class="skeleton-participate-btn" />
          <div class="skeleton-rules-btn" />
        </div>

        <!-- Jogos Selecionados (gamesSection) -->
        <div class="skeleton-games">
          <div class="skeleton-games-thumbs">
            <div
              v-for="t in 5"
              :key="`thumb-${n}-${t}`"
              class="skeleton-thumb"
            />
          </div>
          <div class="skeleton-games-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* Grid igual ao Missions.vue - usando CSS Grid */
.missions-skeleton {
  display: grid;
  /* Mobile: min 150px por card, cresce automaticamente */
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 100%;
}

/* Desktop: min 240px por card */
@media (min-width: 640px) {
  .missions-skeleton {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }
}

/* Card Wrapper - igual ao cardWrapper do MissionDisponiveis */
.skeleton-card-wrapper {
  width: 100%;
  min-width: 0;
  padding-top: 10px;
}

@media (min-width: 640px) {
  .skeleton-card-wrapper {
    padding-top: 14px;
  }
}

/* Card - igual ao missionCard */
.skeleton-card {
  @apply relative flex flex-col items-start rounded-[9px] overflow-visible;
  width: 100%;
  height: 100%;
  padding: 9px;
  min-width: 0;
  gap: 5px;
  background: linear-gradient(180deg, #0b1220 0%, #010716 100%);
  border: 1px solid #1d2329;
}

@media (min-width: 640px) {
  .skeleton-card {
    padding: 14px;
    border-radius: 14px;
    gap: 7px;
  }
}

/* Mission Block (header com imagem + info) */
.skeleton-mission-block {
  @apply flex items-stretch;
  gap: 7px;
}

@media (min-width: 640px) {
  .skeleton-mission-block {
    gap: 10px;
  }
}

/* Imagem */
.skeleton-image {
  @apply flex-shrink-0 rounded-lg;
  width: 63px;
  height: 63px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-image {
    width: 94px;
    height: 94px;
  }
}

/* Info (nome + descrição) */
.skeleton-info {
  @apply flex flex-col items-start justify-around flex-1 min-w-0;
  height: 63px;
}

@media (min-width: 640px) {
  .skeleton-info {
    height: 94px;
  }
}

.skeleton-name {
  @apply w-full rounded;
  height: 12px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
  will-change: opacity;
}

.skeleton-name-line2 {
  @apply w-3/4 rounded;
  height: 12px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.15s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-name,
  .skeleton-name-line2 {
    height: 19px;
  }
}

.skeleton-description {
  @apply w-full rounded;
  height: 10px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
  will-change: opacity;
}

.skeleton-description-line2 {
  @apply w-2/3 rounded;
  height: 10px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.25s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-description,
  .skeleton-description-line2 {
    height: 15px;
  }
}

/* Reward Section */
.skeleton-reward {
  @apply w-full;
  height: 27px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.3s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-reward {
    height: 40px;
    border-radius: 10px;
  }
}

/* Buttons Section */
.skeleton-buttons {
  @apply flex items-center justify-center w-full;
  gap: 4px;
}

@media (min-width: 640px) {
  .skeleton-buttons {
    gap: 6px;
  }
}

.skeleton-participate-btn {
  @apply flex-1;
  height: 21px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.35s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-participate-btn {
    height: 33px;
    border-radius: 6px;
  }
}

.skeleton-rules-btn {
  height: 22px;
  width: 60px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.4s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-rules-btn {
    height: 33px;
    width: 80px;
    border-radius: 7px;
  }
}

/* Games Section */
.skeleton-games {
  @apply flex items-center justify-around w-full;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 700px;
  padding: 4px 6px;
}

@media (min-width: 640px) {
  .skeleton-games {
    border-radius: 1044px;
    padding: 5px 8px;
  }
}

.skeleton-games-thumbs {
  @apply flex items-center;
}

.skeleton-thumb {
  @apply rounded-full flex-shrink-0;
  width: 19px;
  height: 19px;
  margin-right: -12px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.45s;
  will-change: opacity;
}

.skeleton-thumb:last-child {
  margin-right: 0;
}

@media (min-width: 640px) {
  .skeleton-thumb {
    width: 28px;
    height: 28px;
    margin-right: -18px;
  }
}

.skeleton-games-text {
  height: 8px;
  width: 70px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.5s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-games-text {
    height: 13px;
    width: 100px;
  }
}

/* Animação de pulse usando opacity (GPU-accelerated) */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
</style>
